import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Icon from '@lwt-helix/icon';
import { Button } from '@lwt-helix/buttons';
import AgentNameBlock from './AgentNameBlock';
import { agentProductionTerms, moduleRoutes, searchRoutes } from '../../../constants';
import { useQuery } from '../../../common/hooks/location';
import { Routes } from '../../../common/routes/routes';
import {
    agentListingInfoRoute,
    agentProductionListingRoute,
    agentProfileRoute,
    agentProfileTabs,
    coverageListingRoute
} from '../../constants/agentProductionConstants';
import { unmaskUrlVar } from '../../../common/helpers/routes';
import { useSearchStore, useCommonStore } from '../../../store/store';
import { isAgentMetrics } from '../../../constants/app';
import { email, phone } from '@lwt-helix/helix-icon/outlined';
import { HelixIcon } from '@lwt-helix/helix-icon';
import { getEllipsis } from '../helpers/agentProfile';

const AgentContactInfo = (props) => {
    const { agent, index, statusType, isUnits, intervalType, areaType, displayMode, module, shouldShowBackButton } = props;

    const setSelectedModule = useSearchStore(state => state.setSelectedModule);
    const { navIncomingPage } = useCommonStore(state => ({navIncomingPage: state.agentDetailNavigation?.incomingPage}));
    const isAgMovBreakdownRoutes = [searchRoutes.agentBreakdown, searchRoutes.officeListBreakdown].includes(navIncomingPage);

    const {
        agentId,
        officeAddress,
        officeCity,
        officeState,
        officeZipCode,
        agentEmail,
        agentPhone1,
        agentPhone2
    } = agent ?? {};
    const history = useHistory();
    const query = useQuery();
    const location = useLocation();
    const fromOfficeBreakdownAndGrowthTab = location.state?.fromOfficeBreakdownAndGrowthTab;

    const anchorElements = {
        agentEmail: agentEmail ? (
            <a href={`mailto:${agentEmail}`}>{agentEmail}</a>
        ) : (
            agentProductionTerms.notApplicableAbbrv
        ),
        agentPhone1: agentPhone1 ? (
            <a href={`tel:${agentPhone1.split(' ')[0]}`}>{agentPhone1}</a>
        ) : (
            agentProductionTerms.notApplicableAbbrv
        ),
        agentPhone2: agentPhone2 ? (
            <a href={`tel:${agentPhone2.split(' ')[0]}`}>{agentPhone2}</a>
        ) : (
            agentProductionTerms.notApplicableAbbrv
        )
    };

    const handleBackClick = async () => {
        const graphModeParam = query.get('graph');
        const chartModeParam = query.get('chartMode');
        const period = query.get('period');
        const tabIndex = query.get('tabIndex');
        const queryIsShowBarLabels = query.get('isShowBarLabels');
        let location = Routes.PROF_METRICS.BASE;
        let pathname = window.location.pathname;
        const selectedArea = unmaskUrlVar(query.get('selectedArea'));

        if (query.get('incomingPage') === Routes.PROF_METRICS.COVERAGE_LISTING) {
            history.push({
                pathname: coverageListingRoute,
                state: {
                    selectedArea,
                    agent,
                    displayMode
                }
            });
        } else if (period && pathname === agentListingInfoRoute) {
            location = `${agentProductionListingRoute}?id=${agentId}`
                + `&period=${period}`
                + `&tabIndex=${tabIndex || ''}`
                + `&graph=${graphModeParam}`
                + `&chartMode=${chartModeParam}`
                + `&isShowBarLabels=${queryIsShowBarLabels}`
                + `&intervalType=${intervalType}`;
            return history.push(location);
        } else if (pathname === agentProfileRoute) {
            await setSelectedModule(module);
            return history.push(
                isAgMovBreakdownRoutes ? navIncomingPage : moduleRoutes[module],
                { fromOfficeBreakdownAndGrowthTab }
            );
        } else if (index < agentProfileTabs.TRANSACTION_COVERAGE) {
            location = `${agentProfileRoute}?id=${agentId}`
                + `&tabIndex=${index}&graph=${graphModeParam || ''}`
                + `&chartMode=${chartModeParam || ''}`
                + `&graphStatusType=${statusType || ''}`
                + `&isShowBarLabels=${queryIsShowBarLabels}`
                + `&timeInterval=${intervalType}`;
            history.push(location, { fromOfficeBreakdownAndGrowthTab });
        } else if (index === agentProfileTabs.TRANSACTION_COVERAGE) {
            location = `${agentProfileRoute}?id=${agentId}&tabIndex=${index}&graphStatusType=${statusType}&isUnits=${isUnits}&timeInterval=${intervalType}&areaType=${areaType}&displayMode=${displayMode}`;
            history.push(location);
        } else {
            history.push(location);
        }
    };

    const getFormattedAddress = () => {
        const hasAnyValue = officeAddress || officeCity || officeState || officeZipCode;
        if (!hasAnyValue) return agentProductionTerms.notApplicableAbbrv;

        const parts = [
            officeAddress,
            officeCity,
            officeState?.length > 1 ? officeState : null,
            officeZipCode
        ].filter(Boolean);

        return parts.join(', ');
    };

    const InfoField = ({ label, value, icon, showIcon = true, className = '' }) => (
        <div className={`d-flex align-items-center contact-info-field ${className}`}>
            <div className="d-flex helix-body flex-column">
                <span className="gray-text">{label}</span>
                <span className="bold-font-500">
                    {className === 'ellipsis' ? getEllipsis(value) : value}
                </span>
            </div>
            {showIcon && icon && (
                <div className="circle-icon ml-2">
                    <HelixIcon
                        icon={icon}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div className='d-flex align-items-center agent-contact-info-wrapper p-3 w-100 justify-content-between'>
            <div className='d-flex align-items-center w-100'>
                {((isAgentMetrics && shouldShowBackButton) || !isAgentMetrics) && (
                    <Button
                        dataLwtId='back-button'
                        color='secondary'
                        size='lg'
                        className='bg-dark p-1 d-flex align-self-stretch align-items-center'
                        onClick={handleBackClick}
                    >
                        <Icon
                            className='mr-1'
                            iconName='navigate_before'
                            dataLwtId='previous-icon'
                        />
                    </Button>
                )}
                <div className='d-flex w-100 justify-content-between flex-wrap'>
                    <AgentNameBlock selectedAgent={agent} />
                    <div className='d-flex'>
                        {!isAgentMetrics && (
                            <div className='contact-info-grid'>
                                <InfoField
                                    label='Agent ID'
                                    value={agentId ?? agentProductionTerms.notApplicableAbbrv}
                                    showIcon={false}
                                />
                                <InfoField
                                    className='ellipsis'
                                    label='Email'
                                    value={anchorElements.agentEmail}
                                    icon={email}
                                />
                                <InfoField
                                    className='not-ellipsis'
                                    label='Email'
                                    value={anchorElements.agentEmail}
                                    icon={email}
                                />
                                <InfoField
                                    label='Phone 1'
                                    value={anchorElements.agentPhone1}
                                    icon={phone}
                                />
                                <InfoField
                                    label='Phone 2'
                                    value={anchorElements.agentPhone2}
                                    icon={phone}
                                />
                                <InfoField
                                    label='Office'
                                    value={getFormattedAddress()}
                                    showIcon={false}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

AgentContactInfo.propTypes = {
    agent: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    statusType: PropTypes.string,
    isUnits: PropTypes.bool,
    intervalType: PropTypes.string,
    areaType: PropTypes.string,
    displayMode: PropTypes.string,
    module: PropTypes.string.isRequired,
    shouldShowBackButton: PropTypes.bool
};

export default AgentContactInfo;
